import { Injectable, signal, computed } from '@angular/core';
import { Notification, NotificationType, NotificationOptions, DEFAULT_NOTIFICATION_DURATION } from '../models/notification.model';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notificationsSignal = signal<Notification[]>([]);

  // Expose notifications as readonly
  notifications = computed(() => this.notificationsSignal());

  /**
   * Show a success notification
   */
  success(message: string, options?: NotificationOptions): void {
    this.show('success', message, options);
  }

  /**
   * Show an error notification
   */
  error(message: string, options?: NotificationOptions): void {
    this.show('error', message, options);
  }

  /**
   * Show a warning notification
   */
  warning(message: string, options?: NotificationOptions): void {
    this.show('warning', message, options);
  }

  /**
   * Show an info notification
   */
  info(message: string, options?: NotificationOptions): void {
    this.show('info', message, options);
  }

  /**
   * Show a notification with specified type
   */
  show(type: NotificationType, message: string, options?: NotificationOptions): void {
    const id = this.generateId();
    const duration = options?.duration ?? DEFAULT_NOTIFICATION_DURATION[type];

    const notification: Notification = {
      id,
      type,
      message,
      title: options?.title,
      duration
    };

    this.notificationsSignal.update(notifications => [...notifications, notification]);

    // Auto-dismiss if duration > 0
    if (duration > 0) {
      setTimeout(() => this.dismiss(id), duration);
    }
  }

  /**
   * Dismiss a notification by id
   */
  dismiss(id: string): void {
    this.notificationsSignal.update(notifications =>
      notifications.filter(n => n.id !== id)
    );
  }

  /**
   * Dismiss all notifications
   */
  dismissAll(): void {
    this.notificationsSignal.set([]);
  }

  /**
   * Handle HTTP error and show appropriate message
   */
  handleHttpError(error: any, customMessage?: string): void {
    let message = customMessage || 'An unexpected error occurred';

    if (error) {
      // Handle different error formats
      if (typeof error === 'string') {
        message = error;
      } else if (error.error?.message) {
        message = error.error.message;
      } else if (error.error?.title) {
        message = error.error.title;
      } else if (error.message) {
        message = error.message;
      } else if (error.statusText) {
        message = this.getMessageFromStatus(error.status, error.statusText);
      }
    }

    this.error(message);
  }

  /**
   * Get user-friendly message from HTTP status code
   */
  private getMessageFromStatus(status: number, statusText?: string): string {
    switch (status) {
      case 400:
        return 'Invalid request. Please check your input.';
      case 401:
        return 'Your session has expired. Please log in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'A conflict occurred. Please refresh and try again.';
      case 422:
        return 'The data provided is invalid.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
      case 503:
      case 504:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return statusText || 'An unexpected error occurred';
    }
  }

  private generateId(): string {
    return `notification-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }
}
